import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from '@mui/material';
import {
  Add,
  Search,
  LocationOn
} from '@mui/icons-material';
import api from '../services/api';
import LocationCard from '../components/LocationCard';
import LocationForm from '../components/LocationForm';
import { useLocation } from '../context/LocationContext';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  const { refreshLocations } = useLocation();

  useEffect(() => {
    loadLocations();
  }, [statusFilter, searchTerm]);

  const loadLocations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/locations?${params.toString()}`);
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      showSnackbar('Failed to load locations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedLocation(null);
    setOpenModal(true);
  };

  const handleEdit = (location) => {
    setSelectedLocation(location);
    setOpenModal(true);
  };

  const handleSuccess = () => {
    loadLocations();
    refreshLocations();
    showSnackbar(
      selectedLocation ? 'Location updated successfully' : 'Location created successfully',
      'success'
    );
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Locations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your sale locations and branches
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddNew}
        >
          Add Location
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Locations</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="inactive">Inactive Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              {locations.length} location{locations.length !== 1 ? 's' : ''} found
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : locations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LocationOn sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Locations Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first location'}
          </Typography>
          {!searchTerm && statusFilter === 'all' && (
            <Button variant="contained" startIcon={<Add />} onClick={handleAddNew}>
              Add First Location
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {locations.map((location) => (
            <Grid item xs={12} sm={6} md={4} key={location.id}>
              <LocationCard location={location} onEdit={handleEdit} />
            </Grid>
          ))}
        </Grid>
      )}

      <LocationForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        location={selectedLocation}
        onSuccess={handleSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Locations;
